<template>
  <div class="grid grid-cols-2 gap-6 items-start">
    <div class="space-y-3">
      <p class="text-gray-600">Each section is one part of the document. Its instructions tell the AI what to write there.</p>
      <div v-for="(s, i) in modelValue" :key="s.key" :class="['rounded-lg border bg-white', open === s.key ? 'border-primary-500' : 'border-gray-200']" :data-test="`section-${s.key}`">
        <div class="flex items-center gap-2 px-3 py-2">
          <button type="button" class="flex-1 min-w-0 text-left" :data-test="`toggle-${s.key}`" @click="open = open === s.key ? null : s.key">
            <span class="font-medium text-gray-900">{{ s.label || 'Untitled section' }}</span>
            <span class="ml-2 rounded bg-gray-100 px-2 text-gray-700">{{ styleLabel(s.style) }}</span>
            <span v-if="!s.shown" class="ml-2 rounded bg-neutral-100 px-2 text-gray-900">Hidden</span>
            <span v-else-if="!s.inPdf" class="ml-2 rounded bg-neutral-100 px-2 text-gray-900">Not in PDF</span>
          </button>
          <button type="button" :class="TEXT_BUTTON" :disabled="i === 0" aria-label="Move up" @click="move(i, -1)">↑</button>
          <button type="button" :class="TEXT_BUTTON" :disabled="i === modelValue.length - 1" aria-label="Move down" @click="move(i, 1)">↓</button>
          <button type="button" :class="[TEXT_BUTTON, 'ml-2']" :data-test="`remove-${s.key}`" @click="remove(i)">Remove</button>
        </div>
        <div v-if="open === s.key" class="space-y-3 border-t border-gray-200 px-3 py-3">
          <div class="grid grid-cols-3 gap-3">
            <label class="col-span-2 block"><span class="block text-gray-500 mb-1">Heading</span><input :value="s.label" :class="INPUT" data-test="section-label" @input="patch(i, { label: $event.target.value })"></label>
            <label class="block"><span class="block text-gray-500 mb-1">Shown as</span>
              <select :value="s.style" :class="INPUT" data-test="section-style" @change="patch(i, restyle(s, $event.target.value))">
                <option v-for="st in STYLES" :key="st.value" :value="st.value">{{ st.label }}</option>
              </select>
            </label>
          </div>
          <p class="text-gray-500">{{ styleHint(s.style) }}</p>
          <label class="block"><span class="block text-gray-500 mb-1">Instructions for the AI</span>
            <textarea :value="s.description" rows="4" :class="INPUT" data-test="section-description" placeholder="What belongs in this section, and what to leave out." @input="patch(i, { description: $event.target.value })" />
          </label>
          <div v-if="hasFields(s)">
            <div class="text-gray-500 mb-1">{{ s.style === 'table' ? 'Columns' : 'Fields' }}</div>
            <div v-for="(f, j) in s.fields" :key="j" class="flex items-start gap-2 mb-2">
              <input :value="f.name" :class="[INPUT, 'w-40 shrink-0']" :disabled="fixed(s)" aria-label="Field name" @input="patchField(i, j, { name: $event.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })">
              <input :value="f.description" :class="INPUT" placeholder="What the AI writes here" aria-label="Field instructions" @input="patchField(i, j, { description: $event.target.value })">
              <label v-if="s.style === 'table'" class="flex items-center gap-1 whitespace-nowrap text-gray-600 pt-2"><input type="checkbox" :checked="f.column !== false" @change="patchField(i, j, { column: $event.target.checked })"> Column</label>
              <button v-if="!fixed(s)" type="button" :class="[TEXT_BUTTON, 'pt-2']" :disabled="s.fields.length === 1" @click="removeField(i, j)">Remove</button>
            </div>
            <button v-if="!fixed(s)" type="button" :class="TEXT_BUTTON" :data-test="`add-field-${s.key}`" @click="addField(i)">Add {{ s.style === 'table' ? 'column' : 'field' }}</button>
          </div>
          <div class="flex gap-6 border-t border-gray-200 pt-3">
            <label class="flex items-center gap-2"><input type="checkbox" :checked="s.shown" @change="patch(i, { shown: $event.target.checked })"> Show in the document</label>
            <label class="flex items-center gap-2"><input type="checkbox" :checked="s.inPdf" :disabled="!s.shown" @change="patch(i, { inPdf: $event.target.checked })"> Include in the PDF</label>
          </div>
          <p class="text-gray-500">Field name: <span class="font-mono">{{ s.key }}</span></p>
        </div>
      </div>
      <div class="flex items-end gap-2 rounded-lg border border-dashed border-gray-300 p-3">
        <label class="block flex-1"><span class="block text-gray-500 mb-1">New section heading</span><input v-model="newLabel" :class="INPUT" data-test="new-section-label"></label>
        <label class="block"><span class="block text-gray-500 mb-1">Shown as</span>
          <select v-model="newStyle" :class="INPUT"><option v-for="st in STYLES" :key="st.value" :value="st.value">{{ st.label }}</option></select>
        </label>
        <button type="button" :class="SECONDARY_BUTTON" :disabled="!newLabel.trim()" data-test="add-section" @click="add">Add section</button>
      </div>
    </div>
    <div class="sticky top-4">
      <div class="mb-2 flex items-center gap-2 text-gray-500">
        <span>Preview</span>
        <span>· {{ sample ? 'from the last draft run' : 'placeholder text until you run the draft' }}</span>
      </div>
      <div class="max-h-[80vh] overflow-y-auto rounded-lg bg-gray-50 p-4">
        <SummaryDocumentPage :title="title" :sections="previewSections" :content="previewContent" />
      </div>
    </div>
  </div>
</template>

<script>
import SummaryDocumentPage from '@/components/Care_Management/Widgets/PatientEngagement/SummaryDocumentPage.vue';
import { INPUT, SECONDARY_BUTTON, TEXT_BUTTON } from './hermes';
import { STYLES, isFixed, newSection, restyle, sampleContent, shapeOf, usesFields } from './documentSections';

export default {
  name: 'HermesSectionBuilder',
  components: { SummaryDocumentPage },
  props: {
    modelValue: { type: Array, required: true },
    title: { type: String, default: 'Document' },
    // A real result (e.g. from the last draft run) to preview; placeholder text fills anything it lacks.
    sample: { type: Object, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return { open: null, newLabel: '', newStyle: 'sourced_list', STYLES, INPUT, SECONDARY_BUTTON, TEXT_BUTTON };
  },
  computed: {
    previewSections() {
      return this.modelValue.filter((s) => s.shown).map((s) => ({
        key: s.key, label: s.label, style: s.style,
        ...(s.style === 'table' ? { columns: s.fields.filter((f) => f.column !== false).map((f) => f.name) } : {}),
      }));
    },
    previewContent() {
      return { ...sampleContent(this.modelValue), ...(this.sample || {}) };
    },
  },
  methods: {
    restyle,
    styleLabel(style) {
      return (STYLES.find((s) => s.value === style) || { label: style }).label;
    },
    styleHint(style) {
      return (STYLES.find((s) => s.value === style) || {}).hint || '';
    },
    hasFields(s) {
      // Sourced paragraphs carry text plus confidence/quote fields; show them so their instructions are editable.
      return usesFields(s.style) || (shapeOf(s.style) === 'text' && s.fields.length > 0);
    },
    fixed(s) {
      return isFixed(s.style) || (shapeOf(s.style) === 'text');
    },
    emit(next) {
      this.$emit('update:modelValue', next);
    },
    patch(i, changes) {
      this.emit(this.modelValue.map((s, k) => (k === i ? { ...s, ...changes } : s)));
    },
    patchField(i, j, changes) {
      const fields = this.modelValue[i].fields.map((f, k) => (k === j ? { ...f, ...changes } : f));
      this.patch(i, { fields });
    },
    addField(i) {
      const taken = this.modelValue[i].fields.map((f) => f.name);
      let n = taken.length + 1;
      while (taken.includes(`field_${n}`)) n += 1;
      this.patch(i, { fields: [...this.modelValue[i].fields, { name: `field_${n}`, description: '' }] });
    },
    removeField(i, j) {
      this.patch(i, { fields: this.modelValue[i].fields.filter((_, k) => k !== j) });
    },
    move(i, delta) {
      const next = [...this.modelValue];
      [next[i], next[i + delta]] = [next[i + delta], next[i]];
      this.emit(next);
    },
    remove(i) {
      this.emit(this.modelValue.filter((_, k) => k !== i));
    },
    add() {
      const section = newSection(this.newLabel.trim(), this.newStyle, this.modelValue.map((s) => s.key));
      this.emit([...this.modelValue, section]);
      this.open = section.key;
      this.newLabel = '';
    },
  },
};
</script>
